import React, { useEffect, useRef, lazy, Suspense } from 'react';
import { ResponsiveImage } from '../../parts/media/ResponsiveImage';
import { useRecoilValue } from 'recoil';
import { calculationSpecs, productMeasurements } from '@state';
import Flickity from 'react-flickity-component';
import ProductSchematics from './parts/ProductSchematics';
import { slugify } from '@utils';
import './flickity.scss';
import { useMount } from 'ahooks';
import classNames from 'classnames';
import { useMediaQuery } from 'react-responsive';

// const Betsy = lazy(() => import('./schematics/Betsy'));
// const Kelsey = lazy(() => import('./schematics/Kelsey'));
// const Madison = lazy(() => import('./schematics/Madison'));
// const Melissa = lazy(() => import('./schematics/Melissa'));
// const Michelle = lazy(() => import('./schematics/Michelle'));
// const Nathan = lazy(() => import('./schematics/Nathan'));
// const Jennifer = lazy(() => import('./schematics/Jennifer'));
// const Natalie = lazy(() => import('./schematics/Natalie'));
// const Neuroticmom = lazy(() => import('./schematics/Neuroticmom'));
// const Lindsey = lazy(() => import('./schematics/Lindsey'));
// const paige = lazy(() => import('./schematics/Paige'));
// const Gilman = lazy(() => import('./schematics/Gilman'));
// const Rae = lazy(() => import('./schematics/Rae'));
// const Rob = lazy(() => import('./schematics/Rob'));
// const Sydney = lazy(() => import('./schematics/Sydney'));
// const Tyler = lazy(() => import('./schematics/Tyler'));
// const Vera = lazy(() => import('./schematics/Vera'));
// const KitA = lazy(() => import('./schematics/KitA'));
// const KitB = lazy(() => import('./schematics/KitB'));
// const KitC = lazy(() => import('./schematics/KitC'));
// const KitD = lazy(() => import('./schematics/KitD'));
// const KitE = lazy(() => import('./schematics/KitE'));
// const KitG = lazy(() => import('./schematics/KitG'));

function getFilename(url) {
  if (url) {
    return url.split('/').pop().split('#')[0].split('?')[0];
  }
  return '';
}

const VideoPlayer = ({ media, slider, cellIndex }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (slider) {
      slider.on('change', (index) => {
        if (index === cellIndex) {
          videoRef.current?.play();
        } else {
          videoRef.current?.pause();
        }
      });
    }
  }, [videoRef.current, slider]);

  return (
    <video className="video" controls muted loop playsInline poster={media.preview_image.src} ref={videoRef}>
      {media.sources.map((source, index) => {
        return <source src={source.url} type={source.mime_type} key={`video-source-${index}`} />;
      })}
    </video>
  );
};

const ProductGallery = ({ handle, product }) => {
  const { title, variants, media: data } = product;
  const productSpecs = useRecoilValue(calculationSpecs);
  const productType = productSpecs?.product_type?.name;
  let flickityInstance = useRef(null);
  let thumbsFlickityInstance = useRef(null);
  let thumbsWrapper = useRef(null);
  let thumbs = useRef(null);
  const diagramMedia = data?.find((item) => item.alt?.includes('diagram'));
  const otherMedia = data?.filter((item) => !item.alt || !item.alt?.includes('diagram'));

  const isMobile = useMediaQuery({ query: '(max-width: 980px)' });

  const inlineSchematicsComponents = {
    Anesthesia: lazy(() => import('./schematics/Anesthesia')),
    ['Basic Restorative']: lazy(() => import('./schematics/BasicRestorative')),
    ['Burrs']: lazy(() => import('./schematics/Burrs')),
    ['Composite & Cement']: lazy(() => import('./schematics/CompositeCements')),
    ['Disposable Tools']: lazy(() => import('./schematics/DisposableTools')),
    ['Hygiene Disposable']: lazy(() => import('./schematics/HygieneDisposables')),
    ['Hygiene Essentials']: lazy(() => import('./schematics/HygieneEssentials')),
    ['Operatory Tools']: lazy(() => import('./schematics/OperatoryTools')),
    ['OR Essentials']: lazy(() => import('./schematics/OREssentials')),
    ['Patient Hygiene Kits']: lazy(() => import('./schematics/PatientHygieneKits')),
    ['Ortho A']: lazy(() => import('./schematics/KitA')),
    ['Ortho B']: lazy(() => import('./schematics/KitB')),
    ['Ortho C']: lazy(() => import('./schematics/KitC')),
    ['Ortho D']: lazy(() => import('./schematics/KitD')),
    ['Ortho E']: lazy(() => import('./schematics/KitE')),
    ['Ortho G']: lazy(() => import('./schematics/KitG')),
    ['Patient Procedure Supplies 1']: lazy(() => import('./schematics/PatientProcedureSupplies1')),
    ['Patient Procedure Supplies 2']: lazy(() => import('./schematics/PatientProcedureSupplies2')),
    ['Patient Procedure Supplies 3']: lazy(() => import('./schematics/PatientProcedureSupplies3')),
    ['Patient Procedure Supplies 4']: lazy(() => import('./schematics/PatientProcedureSupplies4')),
    ['Patient Procedure Supplies 5']: lazy(() => import('./schematics/PatientProcedureSupplies5')),
    ['Restorative Tray Supplies']: lazy(() => import('./schematics/RestorativeTraySupplies')),
    ['Surgical Tools']: lazy(() => import('./schematics/SurgicalTools')),
  };

  useMount(() => {
    /**
     * Swap main image when user selects a new variant
     */
    window.theme_app.on('variant_selected', (state) => {
      let imageIndex = data?.findIndex((item) => getFilename(item?.src) === getFilename(state.variant.featured_image?.src));
      if (imageIndex > -1) {
        flickityInstance.current?.select(imageIndex);
      }
    });
  });

  useEffect(() => {
    if (thumbsWrapper.current) {
      thumbs.current = thumbsWrapper.current.querySelectorAll('.item');
    }
    return () => {
      thumbs.current = null;
    };
  }, [thumbsWrapper]);

  useEffect(() => {
    /**
     * When deep linking into variants, load relevant image if set
     */
    let currentURL = new URL(window.location.href);
    let variantId = currentURL.searchParams.get('variant');
    if (variantId) {
      let selectedVariant = variants.find((variant) => variant.id === Number(variantId));
      let imageIndex = data?.findIndex((item) => getFilename(item?.src) === getFilename(selectedVariant?.featured_image?.src));
      if (imageIndex > -1) {
        setTimeout(() => {
          flickityInstance.current?.select(imageIndex);
        }, 300);
      }
    }

    flickityInstance.current?.on('change', (index) => {
      handleNextThumb(index);
      thumbsFlickityInstance.current?.select(index);
    });

    return () => {
      flickityInstance.current?.off('change');
    };
  }, [flickityInstance.current]);

  const handleImageSwitch = (index) => {
    flickityInstance?.current?.select(index);
  };

  //Select diagram slider when measurements change
  const measurements = useRecoilValue(productMeasurements);
  useEffect(() => {
    if (diagramMedia) {
      const diagramIndex = data?.findIndex((item) => item.alt?.includes('diagram'));
      if (Boolean(measurements?.width) && flickityInstance?.current?.selectedIndex != diagramIndex) {
        console.log('change image on measurements input');
        flickityInstance?.current?.select(diagramIndex);
      }
    }
  }, [measurements]);

  const handleNextThumb = (index) => {
    if (!thumbs.current) return;
    let previousSlide = Array.from(thumbs.current).find((item) => item.classList.contains('is-selected'));
    if (previousSlide) {
      previousSlide.classList.remove('is-selected');
    }
    let nextSlide = thumbs.current[index];
    nextSlide.classList.add('is-selected');

    const { height: cellHeight, y: cellTop, top: cellScroll } = nextSlide.getBoundingClientRect();
    const { height: navHeight, y: sliderTop, top: sliderScroll } = thumbsWrapper.current.getBoundingClientRect();

    let scrollY = nextSlide.offsetTop + thumbsWrapper.current.scrollTop - (navHeight + cellHeight) / 2;
    thumbsWrapper.current.scrollTop = scrollY;
  };

  const mainSliderOptions = {
    // fade: true,
    prevNextButtons: true,
    pageDots: false,
    adaptiveHeight: true,
  };

  const thumbsSliderOptions = {
    cellAlign: 'left',
    draggable: true,
    prevNextButtons: false,
    pageDots: true,
    watchCSS: true,
    cellSelector: '.item',
  };

  if (!diagramMedia && !otherMedia) {
    return (
      <div className="emptyGallery">
        <svg viewBox="0 0 20 20" className="icon" width="1em" height="1em">
          <use href="#camera"></use>
        </svg>
      </div>
    );
  }

  const Schematics = inlineSchematicsComponents[productSpecs?.name];

  if (diagramMedia && !Boolean(productType)) return null;

  const diagramWrapperClasses = classNames(`sliderItem__diagram`, {
    addPadding: !Boolean(Schematics),
  });

  let hasSchematics = productType === 'Spice Racks' || diagramMedia;

  let renderThumbs = (classModifier) => {
    return (
      <>
        {data?.map((media, index) => {
          return (
            <div
              className={classNames('media-wrapper item', { diagramThumb: media.alt?.includes('diagram') })}
              key={`product-thumbs-${media.id}-${classModifier}}`}
              onClick={() => handleImageSwitch(index)}
              role="button"
            >
              {media.media_type === 'video' && (
                <div className="video-thumb">
                  <svg viewBox="0 0 20 20" className="icon" width="1em" height="1em">
                    <use href="#play"></use>
                  </svg>
                  <img src={media.preview_image.src} />
                </div>
              )}
              {media.media_type === 'image' && (
                <>
                  {media.alt?.includes('diagram') ? (
                    <ResponsiveImage image={diagramMedia} title={title}></ResponsiveImage>
                  ) : (
                    <ResponsiveImage image={media} title={title}></ResponsiveImage>
                  )}
                </>
              )}
            </div>
          );
        })}
      </>
    );
  };

  return (
    <div className={`ProductMain__gallery ${slugify(productType || '')}`}>
      <div className="inner">
        <div className="thumbs">
          {isMobile ? (
            <Flickity
              className="thumbs-wrapper"
              options={thumbsSliderOptions}
              // ref={thumbsWrapper}
              flickityRef={(c) => (thumbsFlickityInstance.current = c)}
              reloadOnUpdate={true}
            >
              {renderThumbs('mobile')}
            </Flickity>
          ) : (
            <div className="thumbs-wrapper" ref={thumbsWrapper}>
              {renderThumbs('desktop')}
            </div>
          )}
        </div>
        <div className="main">
          <Flickity className="gallery" options={mainSliderOptions} flickityRef={(c) => (flickityInstance.current = c)}>
            {data.map((image, index) => {
              return (
                <>
                  {image.alt?.includes('diagram') ? (
                    <>
                      {hasSchematics && (
                        <div className="sliderItem" key={`main-gallery-schematics-${image.id}`}>
                          {Boolean(Schematics) ? (
                            <>
                              <div className={diagramWrapperClasses}>
                                <Suspense fallback={<div>Loading...</div>}>
                                  <Schematics></Schematics>
                                </Suspense>
                              </div>
                            </>
                          ) : (
                            <>
                              {diagramMedia && (
                                <div className={diagramWrapperClasses} key={`main-gallery-diagram-${index}`}>
                                  <ProductSchematics image={diagramMedia} handle={handle}></ProductSchematics>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="sliderItem media" key={`main-gallery-${image.id}`} data-image-id={image.id}>
                      {image.media_type === 'image' && <ResponsiveImage image={image} title={title}></ResponsiveImage>}
                      {image.media_type === 'video' && (
                        <VideoPlayer media={image} slider={flickityInstance.current} cellIndex={index}></VideoPlayer>
                      )}
                    </div>
                  )}
                </>
              );
            })}
          </Flickity>
        </div>
      </div>
    </div>
  );
};

export default ProductGallery;
